import type { CrmAuth, SalesRole } from './crmApi';

// ── The Sales CRM site's sign-in (/crm) ───────────────────────
// Kept for this browser tab (sessionStorage), so a reload doesn't sign you
// out; closing the tab does. The tracker hands its session over the same way
// when someone opens the CRM from the Sales Portal or the admin menu.

export type CrmSession =
  | { kind: 'admin'; name: string; role: 'Admin'; isManager: true; takesLeads: false; adminPassword: string }
  | { kind: 'sales'; id: string; name: string; role: SalesRole; isManager: boolean; takesLeads: boolean; crmToken: string };

const KEY = 'vt_crm_session';
let memory: CrmSession | null = null;   // when the browser blocks storage

export function loadCrmSession(): CrmSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as CrmSession;
  } catch { /* storage blocked */ }
  return memory;
}

export function saveCrmSession(s: CrmSession) {
  memory = s;
  try { sessionStorage.setItem(KEY, JSON.stringify(s)); } catch { /* storage blocked */ }
}

export function clearCrmSession() {
  memory = null;
  try { sessionStorage.removeItem(KEY); } catch { /* storage blocked */ }
}

export const authOf = (s: CrmSession): CrmAuth => (s.kind === 'admin' ? { adminPassword: s.adminPassword } : { crmToken: s.crmToken });

// Has leads of their own to call: a salesperson, a manager taking new leads,
// and a Lead Manager always (the leads the salespeople couldn't reach)
export const takesCalls = (s: CrmSession) => s.kind === 'sales' && (s.takesLeads || s.role === 'Lead Manager');

// badge: which count from crmBadges the menu shows on it
export interface CrmPage { id: string; label: string; short: string; emoji: string; badge?: 'today' | 'requests' | 'waiting' | 'meetings' | 'p2p' }

// What each role gets, in menu order: a short menu, with related pages as
// tabs inside one item (Hubs.tsx). A salesperson's home is Today; a Lead
// Manager's the Lead Desk; a Sales Admin's and the admin's the Sales Overview.
// Managers follow the team on the Monitor (Monitor.tsx).
export function pagesFor(s: CrmSession): CrmPage[] {
  if (!s.isManager) {
    return [
      { id: 'today', label: 'Today', short: 'Today', emoji: '📞', badge: 'today' },
      { id: 'meetings', label: 'Meetings', short: 'Meetings', emoji: '🤝', badge: 'meetings' },
      { id: 'leads', label: 'My Leads', short: 'Leads', emoji: '📋' },
      { id: 'p2p', label: 'My P2P', short: 'P2P', emoji: '🎯', badge: 'p2p' },
      { id: 'followups', label: 'Follow-ups', short: 'Follow-ups', emoji: '🗓️' },
      { id: 'progress', label: 'My Progress', short: 'Progress', emoji: '🔥' },
      { id: 'requests', label: 'Shift & Requests', short: 'Requests', emoji: '📝' },
    ];
  }
  return [
    { id: 'home', label: s.role === 'Lead Manager' ? 'Lead Desk' : 'Sales Overview', short: 'Home', emoji: s.role === 'Lead Manager' ? '🧭' : '🏆' },
    { id: 'monitor', label: 'Monitor', short: 'Monitor', emoji: '📈' },
    { id: 'leads', label: 'Leads', short: 'Leads', emoji: '🗂️' },
    { id: 'add', label: 'Add Leads', short: 'Add', emoji: '➕' },
    { id: 'team', label: 'Team & Shifts', short: 'Team', emoji: '👥' },
    { id: 'requests', label: 'Requests', short: 'Requests', emoji: '📝', badge: 'requests' },
    { id: 'meetings', label: 'Coordinator Meetings', short: 'Meetings', emoji: '🤝', ...(takesCalls(s) ? { badge: 'meetings' as const } : {}) },
    { id: 'p2p', label: 'P2P Board', short: 'P2P', emoji: '🎯', badge: 'p2p' },
    ...(takesCalls(s) ? [{ id: 'calls', label: 'My Calls', short: 'My Calls', emoji: '📞', badge: 'today' as const }] : []),
    { id: 'rules', label: 'Rules', short: 'Rules', emoji: '⚙️' },
  ];
}

// How many sit on the phone's bottom bar; the rest are under More
export const bottomCount = (s: CrmSession) => (s.isManager ? 4 : 5);

export const homePage = (s: CrmSession) => (s.isManager ? 'home' : 'today');

// The old menu's pages (bookmarks, notifications, buttons) → where they are
// now: a page and maybe its tab, or a stage for My Leads
export function resolvePage(s: CrmSession, id: string): { page: string; tab?: string; stage?: string } | null {
  if (!s.isManager) {
    const sales: Record<string, { page: string; tab?: string; stage?: string }> = {
      week: { page: 'progress', tab: 'week' }, dashboard: { page: 'progress', tab: 'numbers' },
      noresponse: { page: 'leads', stage: 'No Response' }, request: { page: 'requests' }, shifts: { page: 'requests' }, holidays: { page: 'requests' },
      fixes: { page: 'requests' }, 'my-attendance': { page: 'requests' }, attendance: { page: 'requests' },
    };
    return sales[id] || null;
  }
  const leadManager = s.role === 'Lead Manager';
  const manager: Record<string, { page: string; tab?: string }> = {
    desk: leadManager ? { page: 'home' } : { page: 'monitor', tab: 'desk' },
    overview: leadManager ? { page: 'monitor', tab: 'overview' } : { page: 'home' },
    // Reports became the Monitor (19 Oct 2026)
    reports: { page: 'monitor' }, numbers: { page: 'monitor', tab: 'numbers' }, status: { page: 'monitor', tab: 'status' }, eod: { page: 'monitor', tab: 'eod' },
    dashboard: { page: 'monitor', tab: 'charts' }, recordings: { page: 'monitor', tab: 'recordings' }, history: { page: 'monitor', tab: 'history' },
    // The waiting list became Junk Leads (28 Oct 2026)
    all: { page: 'leads', tab: 'all' }, waiting: { page: 'leads', tab: 'junk' }, junk: { page: 'leads', tab: 'junk' }, allfollowups: { page: 'leads', tab: 'followups' },
    cold: { page: 'leads', tab: 'closed' }, noresponse: { page: 'leads', tab: 'poll' }, deleted: { page: 'leads', tab: 'deleted' },
    request: { page: 'requests' }, fixes: { page: 'requests', tab: 'fixes' }, shifts: { page: 'team', tab: 'shifts' }, holidays: { page: 'team', tab: 'holidays' },
    attendance: { page: 'team', tab: 'attendance' }, 'att-requests': { page: 'requests', tab: 'attendance' },
    ...(takesCalls(s) ? {
      today: { page: 'calls', tab: 'today' }, followups: { page: 'calls', tab: 'followups' }, week: { page: 'calls', tab: 'week' },
    } : {}),
  };
  return manager[id] || null;
}
